import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type { FirstAnswer } from "./free-plan.server";

/** Creates the free account's business, 10 buyer questions and plan. Idempotent. */
export const setupFreeAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ website: z.string().trim().min(3).max(255), companyName: z.string().max(120).default(""), category: z.string().max(300).default(""), town: z.string().max(80).default(""), local: z.boolean().default(false) }).parse(d))
  .handler(async ({ data, context }) => {
    const token = (getRequestHeader("authorization") ?? "").replace(/^Bearer\s+/i, "");
    const { setupAccount } = await import("./free-plan.server");
    return setupAccount((context as any).userId, token, data);
  });

export const getFirstAnswer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { firstAnswer } = await import("./free-plan.server");
    return firstAnswer((context as any).userId);
  });

/**
 * Free users mark their one job done and give the page address. We check the
 * page is live, then recheck the tracking questions weekly for 8 weeks.
 */
export const markFreeJobDone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    pageUrl: z.string().trim().min(4).max(500),
    promptId: z.string().uuid().optional(),
    question: z.string().max(500).optional(),
    stage: z.string().max(40).nullable().optional(),
    beforeNamed: z.boolean().optional(),
    competitors: z.array(z.string().max(120)).max(10).optional(),
  }).parse(d))
  .handler(async ({ data, context }) => {
    const { markJobDone } = await import("./free-plan.server");
    const fallback = data.promptId && data.question
      ? { promptId: data.promptId, question: data.question, stage: data.stage ?? null, beforeNamed: Boolean(data.beforeNamed), competitors: data.competitors ?? [] }
      : undefined;
    return markJobDone((context as any).userId, data.pageUrl, fallback);
  });
